import { loadStripe } from "@stripe/stripe-js"
import { createBrowserClient } from "@supabase/ssr"

import { CONST } from "@/constants"
import type { MarketplaceMerchants } from "@/data/merchantDataLoader"
import { AdminMerchantProfileData } from "@/app/[locale]/admin/data"
import { MerchantProfileFrontend } from "@/app/types"
import { UserPreferredLanguage } from "@/lib/zenstack/.logical-prisma-client"
import { format, parse } from "date-fns"

export abstract class ClientUtils {
  static formatPrice(value: string | number) {
    // Split into whole and decimal parts
    if (value === "" || value === undefined) return "0.00"
    const [whole, decimal] = value.toString().split(".")

    let formattedValue = whole

    // Add or pad decimal places
    if (!decimal) {
      if (value === "0") return "0"
      formattedValue += ".00"
    } else if (decimal.length === 1) {
      formattedValue += `.${decimal}0`
    } else {
      formattedValue += `.${decimal.slice(0, 2)}`
    }

    return formattedValue
  }

  static formatCurrency(value: string | number) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "CAD",
    }).format(Number(value))
  }

  private static _numbers = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"]

  // eslint-disable-next-line prettier/prettier
  private static _alphas = ["a", "b", "c", "d", "e", "f", "g", "h", "i", "j", "k", "l", "m", "n", "o", "p", "q", "r", "s", "t", "u", "v", "w", "x", "y", "z"]

  static handleKeyPress(e: React.KeyboardEvent<HTMLInputElement>, allowList: ("Backspace" | "Num" | "Alpha")[]) {
    if (e.key == "Backspace" && !allowList.includes(e.key)) {
      e.preventDefault()
    }
    if (ClientUtils._numbers.includes(e.key) && !allowList.includes("Num")) {
      e.preventDefault()
    }
    if (ClientUtils._alphas.includes(e.key) && !allowList.includes("Alpha")) {
      e.preventDefault()
    }
  }

  static generateRandomColor() {
    return "#" + ((Math.random() * 0xffffff) << 0).toString(16).padStart(6, "0")
  }

  static formatTime(time: string) {
    const [hours, minutes] = time.split(":")
    const hour = parseInt(hours)
    const period = hour >= 12 ? "p.m." : "a.m."
    const formattedHour = hour % 12 || 12
    return `${formattedHour}${minutes === "00" ? "" : ":" + minutes}${period}`
  }

  static getMerchantLocation(
    merchant: MerchantProfileFrontend | MarketplaceMerchants[number] | AdminMerchantProfileData
  ) {
    return `${merchant.street}, ${merchant.city}, ${merchant.state}, ${merchant.zip}`
  }

  static getBucketResourceBase64OrUrl(bucketPath: string) {
    const isBase64 = bucketPath.startsWith("data:image")
    const getBucketPublicUrl = (bucketName: string, bucketPath: string) => {
      const supabase = ClientUtils.getClientSupabase()
      return supabase.storage.from(bucketName).getPublicUrl(bucketPath).data.publicUrl
    }

    const url = isBase64 ? bucketPath : getBucketPublicUrl(CONST.BUCKET_STORAGE.MERCHANT_BUCKET.name, bucketPath)
    return { isBase64, url }
  }

  static getClientSupabase() {
    return createBrowserClient(process.env.NEXT_PUBLIC_SUPABASE_URL!, process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!)
  }

  static getClientStripe() {
    return loadStripe(CONST.ENV_STRIPE_PUBLISHABLE_KEY())
  }

  /**
   * Get the dirty fields of a React Hook Form useForm.
   * Right now there's no native way to do this, so we're using this helper function.
   * @see https://stackoverflow.com/questions/77310521/how-to-only-submit-data-change-in-react-hook-form
   */
  static dirtyValues(dirtyFields: object | boolean, allValues: object): object {
    // If *any* item in an array was modified, the entire array must be submitted, because there's no way to indicate
    // "placeholders" for unchanged elements. `dirtyFields` is `true` for leaves.
    if (dirtyFields === true || Array.isArray(dirtyFields)) return allValues
    // Here, we have an object
    return Object.fromEntries(
      // @ts-expect-error
      Object.keys(dirtyFields).map((key) => [key, ClientUtils.dirtyValues(dirtyFields[key], allValues[key])])
    )
  }

  static getLocalizedValue<T extends { [key in UserPreferredLanguage]: string }>(
    locale: UserPreferredLanguage,
    values: T
  ) {
    return values[locale]
  }

  static getLocalizedWeekDays(locale: UserPreferredLanguage) {
    return locale === "fr" ? CONST.MERCHANT_DAYS_FR : CONST.MERCHANT_DAYS
  }

  static parseAndFormatOpeningHourFormat(time: string) {
    return format(parse(time, "HH:mm", new Date()), "h:mm a")
  }
}
